/**
 * DashboardActivity Component
 *
 * Displays recent activity feed
 */

import React from 'react';
import { Typography, List, Avatar, Skeleton, Tag, Button } from 'antd';
import { MdPerson } from 'react-icons/md';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ActivityItem } from '../types';

const { Title, Text } = Typography;

interface DashboardActivityProps {
  activities?: ActivityItem[];
  isLoading?: boolean;
}

export const DashboardActivity: React.FC<DashboardActivityProps> = ({
  activities = [],
  isLoading = false,
}) => {
  const navigate = useNavigate();

  return (
    <div className="shadow-none rounded-lg bg-white">
      <div className="flex justify-between items-center p-4">
        <div>
          <Title level={5} className="!mb-1">
            Recent Activity
          </Title>
          <Text type="secondary" className="text-sm">
            Latest quote updates and actions
          </Text>
        </div>
        <Button type="link" size="small" onClick={() => navigate('/quotes')}>
          View All
        </Button>
      </div>

      {isLoading ? (
        <List
          dataSource={[1, 2, 3, 4, 5]}
          renderItem={() => (
            <List.Item className='px-4'>
              <Skeleton avatar paragraph={{ rows: 1 }} active />
            </List.Item>
          )}
        />
      ) : activities && activities.length > 0 ? (
        <List
          dataSource={activities}
          renderItem={(activity) => (
            <List.Item
              className="cursor-pointer hover:bg-gray-50 px-4  transition-colors"
              onClick={() => navigate(`/quotes/${activity.id}`)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar className="bg-primary-100 text-primary-600">
                    <MdPerson size={20} />
                  </Avatar>
                }
                title={
                  <div className="flex items-center gap-2 flex-wrap">
                    <Text strong>{activity.user}</Text>
                    <Text>{activity.action} quote</Text>
                    <Tag color={activity.actionColor}>
                      {activity.quote.referenceNumber}
                    </Tag>
                  </div>
                }
                description={
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <Text type="secondary">{activity.quote.customerName}</Text>
                    <Text type="secondary">•</Text>
                    <Text type="secondary">
                      {new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP',
                        minimumFractionDigits: 0,
                      }).format(activity.quote.amount)}
                    </Text>
                    <Text type="secondary">•</Text>
                    <Text type="secondary">
                      {formatDistanceToNow(new Date(activity.timestamp), {
                        addSuffix: true,
                      })}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <div className="py-8 text-center">
          <Text type="secondary">No recent activity</Text>
        </div>
      )}
    </div>
  );
};

export default DashboardActivity;
